import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Skeleton from '@material-ui/lab/Skeleton';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { default as MuiButton } from '@material-ui/core/Button';

import {
    ContainedButton,
    CalendarIcon,
    CalendarIcon2,
    Text,
    Avatar,
    FileIcon,
    UserIcon,
    PlusCircleIcon,
    StarIcon,
    ConfirmDialog,
    WalletIcon,
    ArrowRight,
    StyledCard,
    StyledCardContent,
    MessageIcon3,
} from '../../index';

import {
    ProviderSidebarContainer,
    StyledProviderMenuButton,
    StyledSignOutButton,
    StyledIconButtonContainer,
    StyledBalanceContainer,
} from './style';

const FirdayLogo = '/resources/images/friday_logo.svg';

import { GET_CREDIT } from '../../../services/provider';
import socket from '../../../socket';
import { alertError } from '../../../redux/stateActions';

export const ProviderSidebar = ({ user, $avatarSize }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [staffID, setStaffID] = useState(null);
    const [providerAvatar, setProviderAvatar] = useState(null);
    const [showSignOutDialog, setShowSignOutDialog] = useState(false);
    const [credit, setCredit] = useState(0);

    const routes = useMemo(() => {
        if (!_.isEmpty(user)) {
            if (['Admin', 'Provider Admin'].includes(user.staff.staff_role.name)) {
                return [
                    {
                        name: 'Session',
                        path: 'session',
                        state: { tab: 0 },
                        icon: CalendarIcon,
                    },
                    {
                        name: 'Calendar',
                        path: 'calendar',
                        icon: CalendarIcon2,
                    },
                    {
                        name: 'Message',
                        path: 'message',
                        icon: MessageIcon3,
                    },
                    {
                        name: 'Client List',
                        path: 'client',
                        icon: UserIcon,
                    },
                    {
                        name: 'Counseling Service Detail',
                        path: 'consultant',
                        icon: PlusCircleIcon,
                    },
                    {
                        name: 'Credits',
                        path: 'credits',
                        icon: StarIcon,
                    },
                ];
            }

            if (user.staff.staff_role.name === 'Provider') {
                return [
                    {
                        name: 'Session',
                        path: 'session',
                        state: { tab: 0 },
                        icon: CalendarIcon,
                    },
                    {
                        name: 'Message',
                        path: 'message',
                        icon: MessageIcon3,
                    },
                    {
                        name: 'Calendar',
                        path: 'calendar',
                        icon: CalendarIcon2,
                    },
                    {
                        name: 'Client List',
                        path: 'client',
                        icon: UserIcon,
                    },
                ];
            }
        }

        return [];
    }, [user]);

    const currentPath = useMemo(() => {
        return location.pathname.split('/')[2];
    }, [location]);

    useEffect(() => {
        if (!_.isEmpty(user.staff)) {
            setStaffID(user.staff.id);
            setProviderAvatar(user.staff?.hospital?.avatar?.url);
        }
    }, [user]);

    const handleSingOut = () => {
        localStorage.clear();
        window.location.replace('/provider/signin');
    };

    /*--------------- Get Credit ---------------*/
    const {
        error: getCreditError,
        data: getCreditData,
        refetch: getCreditRefetch,
    } = useQuery(GET_CREDIT, {
        variables: { id: user.staff.hospital.id },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        if (getCreditError) {
            dispatch(alertError(`Cannot get Credit : ${getCreditError.message}`));
        }

        if (getCreditData && !_.isEmpty(getCreditData.hospital)) {
            setCredit(getCreditData.hospital.credit);
        }
    }, [getCreditData, getCreditError]);

    useEffect(() => {
        socket.on('updateCredit', () => {
            getCreditRefetch();
        });
    }, []);

    return (
        ![undefined, '', 'call', 'groupcall', 'opencall', 'other-opencall', 'invoice'].includes(
            currentPath
        ) && (
            <ProviderSidebarContainer>
                <ConfirmDialog
                    id="confirm-signout"
                    description="modal-for-confirm-signout"
                    open={showSignOutDialog}
                    contentText="Are you sure you want to sign out ?"
                    cancelText="Cancel"
                    onCancel={() => setShowSignOutDialog(false)}
                    confirmText="Sign out"
                    confirmBackgroundColor="pink"
                    onConfirm={handleSingOut}
                />
                <div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            columnGap: '20px',
                            marginBottom: '20px',
                            maxHeight: '35px',
                        }}
                    >
                        <img src={FirdayLogo} alt="friday" />
                        {providerAvatar && (
                            <img
                                src={providerAvatar}
                                alt="provider-avatar"
                                style={{ maxHeight: '35px', width: 'auto' }}
                            />
                        )}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <div style={{ position: 'relative' }}>
                            {!_.isEmpty(user) ? (
                                <Fragment>
                                    <Avatar
                                        alt={`${user?.fullname}`}
                                        src={user.avatar ? user.avatar.url : null}
                                        $avatarSize={$avatarSize ? $avatarSize : '80px'}
                                        $borderRadius={'40px'}
                                    />
                                    <Link to={`/provider/profile/${staffID}`}>
                                        <ContainedButton
                                            style={{
                                                padding: '5px',
                                                width: '24px',
                                                height: '24px',
                                                minWidth: 0,
                                                position: 'absolute',
                                                bottom: '0',
                                                right: '0',
                                            }}
                                            $backgroundColor={
                                                location.pathname === `/provider/profile/${staffID}`
                                                    ? 'purple'
                                                    : 'primaryBlue'
                                            }
                                        >
                                            <FileIcon />
                                        </ContainedButton>
                                    </Link>
                                </Fragment>
                            ) : (
                                <Skeleton variant="circle" width={80} height={80} />
                            )}
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: '10px',
                        }}
                    >
                        {!_.isEmpty(user) ? (
                            <Fragment>
                                <Text $fontSize="18px">{user?.fullname}</Text>
                                <Text $fontSize="14px" $color="gray4">
                                    {user.staff?.staff_position?.name}
                                </Text>
                                <StyledBalanceContainer>
                                    <WalletIcon fill="gray6" />
                                    <Text $color="gray2" $bold>
                                        {credit.toLocaleString()}
                                    </Text>
                                    {user.staff.staff_role.name !== 'Provider' && (
                                        <Link to={`/provider/credits`}>
                                            <ArrowRight fill="gray2" />
                                        </Link>
                                    )}
                                </StyledBalanceContainer>
                            </Fragment>
                        ) : (
                            <Fragment>
                                <Skeleton width={150} height={30} variant="text" />
                                <Skeleton width={120} height={30} variant="text" />
                                <Skeleton width={120} height={30} variant="text" />
                            </Fragment>
                        )}
                    </div>
                </div>
                <div
                    style={{
                        marginTop: '50px',
                        paddingRight: '8px',
                    }}
                >
                    {!_.isEmpty(routes) &&
                        routes.map((route) => (
                            <Link
                                to={{
                                    pathname: `/provider/${route.path}`,
                                    state: { ...route.state },
                                }}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    textDecoration: 'none',
                                }}
                                key={route.name}
                            >
                                <StyledProviderMenuButton selected={route.path === currentPath}>
                                    <StyledIconButtonContainer
                                        $backgroundColor={
                                            route.path === currentPath ? 'primaryBlue' : 'gray4'
                                        }
                                    >
                                        <route.icon fill="white" width="20" height="20" />
                                    </StyledIconButtonContainer>
                                    <Text
                                        $color={route.path === currentPath ? 'black' : 'gray4'}
                                        style={{ cursor: 'pointer', textAlign: 'left' }}
                                    >
                                        {route.name}
                                    </Text>
                                </StyledProviderMenuButton>
                            </Link>
                        ))}
                    <StyledSignOutButton onClick={() => setShowSignOutDialog(true)}>
                        <Text $color="gray4">Log Out</Text>
                    </StyledSignOutButton>
                </div>
            </ProviderSidebarContainer>
        )
    );
};

ProviderSidebar.propTypes = {
    user: PropTypes.object,
    $avatarSize: PropTypes.string,
};

export const PopupMenu = ({ open, top, right, bottom, left, options, onClose, disabled }) => {
    return (
        open && (
            <StyledCard
                style={{
                    padding: '8px',
                    position: 'absolute',
                    width: 'auto',
                    backgroundColor: 'white',
                    zIndex: 200,
                    top,
                    right,
                    bottom,
                    left,
                    boxShadow: '-1px 4px 18px rgba(95, 102, 140, 0.23)',
                }}
            >
                <StyledCardContent
                    style={{ display: 'flex', flexDirection: 'column', width: 'auto' }}
                >
                    {options.map((option) => (
                        <ActionMenuButton
                            key={option.text}
                            text={option.text}
                            onClick={() => {
                                option.onClick();
                                if (onClose) {
                                    onClose();
                                }
                            }}
                            disabled={disabled}
                        />
                    ))}
                </StyledCardContent>
            </StyledCard>
        )
    );
};

PopupMenu.propTypes = {
    options: PropTypes.arrayOf(PropTypes.object).isRequired,
    open: PropTypes.bool.isRequired,
    top: PropTypes.string,
    right: PropTypes.string,
    bottom: PropTypes.string,
    left: PropTypes.string,
    onClose: PropTypes.func,
    disabled: PropTypes.bool,
};

const ActionMenuButton = ({ text, onClick, disabled }) => {
    return (
        <MuiButton
            style={{
                borderRadius: '6px',
                width: '150px',
                height: '35px',
                justifyContent: 'flex-start',
                textTransform: 'none',
                paddingLeft: '10px',
            }}
            disabled={disabled}
            onClick={onClick}
        >
            <Text $fontSize="14px">{text}</Text>
        </MuiButton>
    );
};

ActionMenuButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    text: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
};
